import type { DeleteDocumentFilesMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteDocumentFilesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface DeleteDocumentFilesInput {
  documentFileIds: string[];
}

export const useDeleteDocumentFiles = createMutationHook({
  name: 'deleteDocumentFiles',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.documentFile.delete.mutationOptions({ onSuccess }),
  mapInput: (variables: DeleteDocumentFilesInput) => ({
    Ids: variables.documentFileIds,
  }),
  invalidate: (trpc) => [
    trpc.frontend.documentFile.documentFilesByDocumentId.queryKey(),
    trpc.frontend.documentFile.latestDocumentFile.queryKey(),
    trpc.frontend.documentFile.documentFileById.queryKey(),
    trpc.frontend.documentFile.publicDocumentFiles.queryKey(),
    trpc.frontend.documentFile.latestPublicDocumentFileByDocumentId.queryKey(),
    // COI-889: both siblings declare this for COI-471 parity — deleting the
    // latest or published version moves the document's review date and status.
    trpc.frontend.document.pathKey(),
    // COI-889: deleting a Published version breaks the
    // `node.documentFile.Status = published` join that
    // `getRibbonAttestationRecords` requires, so its records drop off the
    // ribbon's attestation tile.
    trpc.frontend.myItems.pathKey(),
  ],
  /**
   * Maps the TRPC mutation response (`{ Ids: string[] }`) to match the legacy
   * GraphQL shape (`{ delete_file: { affected_rows }, delete_document_file: { affected_rows } }`)
   * so consumers can be migrated 1:1.
   *
   * `delete_document_file.affected_rows` = `Ids.length` (deleted document_file rows).
   * `delete_file.affected_rows` is set to 0 — the cascaded `file` row count is
   * not surfaced by the data-layer today. Html/Link versions have no `file`
   * row, so reusing `Ids.length` for both fields (the prior implementation)
   * would have inflated the count. Consumers currently only branch on
   * success/error, not on the count, so 0 is safe; if a follow-up needs the
   * exact file count it should be added to the data-layer response.
   */
  mapResponse: (result: { Ids: string[] }): DeleteDocumentFilesMutation => ({
    delete_file: {
      __typename: 'file_mutation_response',
      affected_rows: 0,
    },
    delete_document_file: {
      __typename: 'document_file_mutation_response',
      affected_rows: result.Ids.length,
    },
  }),
  graphqlDocument: DeleteDocumentFilesDocument,
  graphqlVariables: (variables: DeleteDocumentFilesInput) => ({
    ...variables,
  }),
  evictFields: ['document_file'],
});
